import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme, alpha, Avatar } from '@mui/material';
import { ChevronDown, Subtitles } from 'lucide-mui';
import TimestampChip from '../../../common/TimestampChip';
import { getLetterAvatar } from '../../../../lib/commonUtils';

// Demo data that showcases transcript features
const demoSegments = [
  {
    id: 1,
    speaker: 'Speaker 1',
    speakerColor: '#1976d2',
    start: 0,
    end: 4.2,
    text: 'Welcome to our quarterly review meeting.',
    words: [
      { word: 'Welcome', start: 0.0, end: 0.8 },
      { word: 'to', start: 0.8, end: 1.0 },
      { word: 'our', start: 1.0, end: 1.3 },
      { word: 'quarterly', start: 1.3, end: 2.1 },
      { word: 'review', start: 2.1, end: 2.8 },
      { word: 'meeting.', start: 2.8, end: 4.2 },
    ],
  },
  {
    id: 2,
    speaker: 'Speaker 2',
    speakerColor: '#d32f2f',
    start: 4.5,
    end: 8.3,
    text: 'Thanks John. Let me share the latest sales numbers.',
    words: [
      { word: 'Thanks', start: 4.5, end: 5.0 },
      { word: 'John.', start: 5.0, end: 5.4 },
      { word: 'Let', start: 5.7, end: 5.9 },
      { word: 'me', start: 5.9, end: 6.1 },
      { word: 'share', start: 6.1, end: 6.6 },
      { word: 'the', start: 6.6, end: 6.8 },
      { word: 'latest', start: 6.8, end: 7.3 },
      { word: 'sales', start: 7.3, end: 7.8 },
      { word: 'numbers.', start: 7.8, end: 8.3 },
    ],
  },
  {
    id: 3,
    speaker: 'Speaker 1',
    speakerColor: '#1976d2',
    start: 8.8,
    end: 12.1,
    text: 'Perfect. I see we exceeded our targets this quarter.',
    words: [
      { word: 'Perfect.', start: 8.8, end: 9.5 },
      { word: 'I', start: 9.8, end: 9.9 },
      { word: 'see', start: 9.9, end: 10.2 },
      { word: 'we', start: 10.2, end: 10.4 },
      { word: 'exceeded', start: 10.4, end: 11.0 },
      { word: 'our', start: 11.0, end: 11.2 },
      { word: 'targets', start: 11.2, end: 11.7 },
      { word: 'this', start: 11.7, end: 11.9 },
      { word: 'quarter.', start: 11.9, end: 12.1 },
    ],
  },
];

const TranscriptDemo: React.FC = () => {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Simulate video playback for demo
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentTime((prev) => {
          const next = prev + 0.1;
          // Loop the demo after 15 seconds
          return next > 15 ? 0 : next;
        });
      }, 100);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying]);

  // Auto-start demo after a delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPlaying(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const formatTimestamp = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // SpeakerAvatar component matching the exact styling from SpeakerSelect
  const SpeakerAvatar = ({ name, color }: { name: string; color: string }) => (
    <Avatar
      alt={name}
      sx={{
        width: 28,
        height: 28,
        bgcolor: color,
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
    >
      {getLetterAvatar(name)}
    </Avatar>
  );

  // SpeakerName component matching the exact styling from SpeakerSelect
  const SpeakerName = ({ name }: { name: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontSize: '0.875rem',
          lineHeight: 1.43,
        }}
      >
        {name}
      </Typography>
    </Box>
  );

  // SpeakerSelect component matching the exact styling from the real component
  const SpeakerSelect = ({
    speaker,
    color,
  }: {
    speaker: string;
    color: string;
  }) => (
    <Box
      data-testid="speaker-select"
      sx={{
        display: 'flex',
        alignItems: 'center',
        columnGap: theme.spacing(0.75),
        cursor: 'pointer',
        padding: theme.spacing(0.75, 1.5, 0.75, 0.5),
        borderRadius: '24px',
        height: '40px',
        transition: theme.transitions.create(['background-color', 'width'], {
          duration: theme.transitions.duration.shorter,
        }),
        width: 'auto',
        background: 'transparent',
        '&:hover': {
          backgroundColor: `${theme.palette.background.paper} !important`,
        },
        '&.active': {
          backgroundColor: `${theme.palette.background.paper} !important`,
        },
      }}
    >
      <SpeakerAvatar name={speaker} color={color} />
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <SpeakerName name={speaker} />
        <ChevronDown
          style={{
            width: 16,
            height: 16,
            marginLeft: 4,
            color: theme.palette.text.secondary,
          }}
        />
      </Box>
    </Box>
  );

  const renderWordLevelText = (segment: typeof demoSegments[0]) => {
    return (
      <Typography component="span" sx={{ lineHeight: 1.5 }}>
        {segment.words.map((word, index) => {
          const isCurrentWord =
            currentTime >= word.start && currentTime <= word.end;
          const isLastWord = index === segment.words.length - 1;

          return (
            <React.Fragment key={`${segment.id}-${index}`}>
              <span
                style={{
                  backgroundColor: isCurrentWord
                    ? theme.palette.primary.main
                    : 'transparent',
                  color: isCurrentWord
                    ? theme.palette.primary.contrastText
                    : 'inherit',
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  padding: '1px 0.5px',
                  borderRadius: '2px',
                  cursor: 'pointer',
                }}
              >
                {word.word}
              </span>
              {!isLastWord && ' '}
            </React.Fragment>
          );
        })}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          pb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Subtitles sx={{ fontSize: 20 }} />
          Live Transcript
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2,
          }}
        >
          See every word as it&apos;s spoken with precise timing
        </Typography>
      </Box>

      {/* Transcript content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(theme.palette.action.hover, 0.1),
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.action.hover, 0.3),
            borderRadius: 3,
          },
        }}
      >
        {demoSegments.map((segment) => {
          const isCurrentSegment =
            currentTime >= segment.start && currentTime <= segment.end;

          return (
            <Box
              key={segment.id}
              data-segment-id={segment.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: theme.spacing(0.75, 1, 1, 1),
                position: 'relative',
                borderRadius: isCurrentSegment ? '16px' : '0px',
                backgroundColor: isCurrentSegment
                  ? alpha(theme.palette.primary.main, 0.05)
                  : 'transparent',
                transition: 'border-radius 0.2s, background-color 0.2s',
                mb: 1,
              }}
            >
              {/* Header with speaker and timestamp - exact styling from Segment.tsx */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  columnGap: theme.spacing(1),
                  marginBottom: theme.spacing(1),
                }}
              >
                {/* Speaker select with wrapper div like in Segment.tsx */}
                <Box
                  sx={{
                    background: 'transparent',
                    '& > div': {
                      backgroundColor: 'transparent !important',
                      '&:hover': {
                        backgroundColor: `${theme.palette.background.paper} !important`,
                      },
                      '&.active': {
                        backgroundColor: `${theme.palette.background.paper} !important`,
                      },
                    },
                  }}
                >
                  <SpeakerSelect
                    speaker={segment.speaker}
                    color={segment.speakerColor}
                  />
                </Box>

                {/* Timestamp with exact wrapper structure from Segment.tsx */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'text.secondary',
                    }}
                  >
                    <TimestampChip
                      timestamp={formatTimestamp(segment.start)}
                      onClick={() => {}}
                      compact
                      sx={{
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Text content with word-level highlighting */}
              <Box
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.action.hover, 0.1),
                    borderRadius: '8px',
                  },
                  p: 1,
                  transition: 'background-color 0.2s ease',
                }}
              >
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={{
                    lineHeight: 1.5,
                    color: 'text.primary',
                  }}
                >
                  {renderWordLevelText(segment)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default TranscriptDemo;
